import { Routes, Route, Navigate } from "react-router-dom";

import { lazy, Suspense, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import ProtectedRoute from "./auth/ProtectedRoute";
import RequireAdmin from "./auth/RequireAdmin";
import RequireManagerOrAdmin from "./auth/RequireManagerOrAdmin";
import { AppLoadingProvider } from "./context/AppLoadingContext";
import {
  StaffTopBandProvider,
  StaffTopBandSlot,
} from "./components/ui/StaffTopBandContext";

import RegulatoryPage from "./pages/RegulatoryPage";
import LoginPage from "./pages/LoginPage";
import DisclaimerAcceptancePage from "./pages/DisclaimerAcceptancePage";
import ClientsPage from "./pages/ClientsPage";
import KycFormPage from "./pages/KycFormPage";
import KycViewPage from "./pages/KycViewPage";
import KycBisPage from "./pages/KycBisPage"; // temporary PEP layout experiment
import KycBis2Page from "./pages/KycBis2Page"; // temporary PEP layout experiment 2

import KycClientSideFormPage from "./pages/KycClientSideFormPage"; // public
import KycClientSideFormMobilePage from "./pages/KycClientSideFormMobilePage";

import KycInboxPage from "./pages/KycInboxPage";
import KycReviewPage from "./pages/KycReviewPage";

import AdminUsersPage from "./pages/AdminUsersPage";

import AdminRiskSettingsPage from "./pages/AdminRiskSettingsPage";

import AdminSourcesUploadPage from "./pages/AdminSourcesUploadPage";
import AdminAmlPolicyPage from "./pages/AdminAmlPolicyPage";
import AdminFatcaRulesPage from "./pages/AdminFatcaRulesPage";

// import SetPasswordPage from "./pages/SetPasswordPage";
import ActivateAccountPage from "./pages/ActivateAccountPage";

// import OpenSanctionsTestPage from "./pages/OpenSanctionsTestPage";

// import OrgChartExample from "./pages/OrgChartExample";

import OwnershipTreeUae from "./kyc/sections/common/OwnershipTreeUae";

import ClientDocumentsUploadPage from "./pages/ClientDocumentsUploadPage";
import ClientDocumentsUploadMobilePage from "./pages/ClientDocumentsUploadMobilePage";
import ClientUploadThanksPage from "./pages/ClientUploadThanksPage";

import StaffDocumentsReviewPage from "./pages/StaffDocumentsReviewPage";

// import ActivitiesCategoryTestPage from "./pages/ActivitiesCategoryTestPage";

// import BlockedPage from "./pages/BlockedPage";

// PRIME REACT CUSTOM ALERT BOX
import AppConfirmDialog from "./components/ui/AppConfirmDialog";
import "./components/ui/css/ConfirmDialog.css";

const FatcaPage = lazy(() => import("./pages/FatcaPage"));

export default function App() {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const previousPath = previousPathRef.current;

  useEffect(() => {
    previousPathRef.current = location.pathname;
  }, [location.pathname]);

  const isKycFormPath = (path) => /^\/kyc\/[^/]+$/.test(String(path || ""));
  const isKycViewPath = (path) => /^\/kyc-view\/[^/]+$/.test(String(path || ""));
  const isKycReviewPath = (path) => /^\/kyc-review\/[^/]+$/.test(String(path || ""));
  const isDocsReviewPath = (path) => /^\/docsReview\/[^/]+$/.test(String(path || ""));
  const isKycDetailPath = (path) => isKycFormPath(path) || isKycViewPath(path);
  const isAuthFlowPath = (path) =>
    path === "/login" || path === "/activate" || path === "/accept-disclaimer";
  const isStaffTopBandPath =
    location.pathname === "/clients" ||
    location.pathname === "/regulatory" ||
    location.pathname === "/fatca" ||
    location.pathname === "/fatca/w8bene" ||
    location.pathname === "/fatca/w8imy" ||
    location.pathname === "/fatca/classification" ||
    location.pathname === "/fatca/igas" ||
    location.pathname === "/admin/fatca" ||
    location.pathname === "/kyc-inbox" ||
    isKycFormPath(location.pathname) ||
    isKycViewPath(location.pathname) ||
    isDocsReviewPath(location.pathname);
  const isStaffViewportLockedPath =
    location.pathname === "/clients" || location.pathname === "/kyc-inbox";
  const isKycFormViewTransition =
    (isKycFormPath(previousPath) && isKycViewPath(location.pathname)) ||
    (isKycViewPath(previousPath) && isKycFormPath(location.pathname));
  const isKycInboxReviewTransition =
    (previousPath === "/kyc-inbox" && isKycReviewPath(location.pathname)) ||
    (isKycReviewPath(previousPath) && location.pathname === "/kyc-inbox");
  const isClientsKycTransition =
    (previousPath === "/clients" && isKycDetailPath(location.pathname)) ||
    (isKycDetailPath(previousPath) && location.pathname === "/clients") ||
    isKycFormViewTransition ||
    isKycInboxReviewTransition;
  const isAuthFlowTransition =
    isAuthFlowPath(previousPath) || isAuthFlowPath(location.pathname);
  const routeMotion = isAuthFlowTransition
    ? {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
        transition: {
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1],
        },
      }
    : {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.22, ease: "easeInOut" },
      };

  return (
    <AppLoadingProvider>
      <StaffTopBandProvider>
        <AppConfirmDialog group="appConfirm" />

      <div
        style={{
          minHeight: "100vh",
          background: location.pathname === "/kyc-inbox"
            ? "#F3F4F5"
            : isAuthFlowPath(location.pathname) || isAuthFlowPath(previousPath)
            ? "#c9d7e0"
            : isStaffTopBandPath || isClientsKycTransition
            ? "#EDF1F4"
            : undefined,
        }}
      >
        <StaffTopBandSlot
          show={isStaffTopBandPath}
          className={location.pathname === "/kyc-inbox" ? "staffTopBandHostInbox" : ""}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={routeMotion.initial}
            animate={routeMotion.animate}
            exit={routeMotion.exit}
            transition={routeMotion.transition}
            style={
              isStaffViewportLockedPath
                ? {
                    height: "calc(100vh - var(--staff-top-band-height, 154px))",
                    minHeight: 0,
                    overflow: "hidden",
                    overscrollBehavior: "none",
                  }
                : { minHeight: "100vh" }
            }
          >
            <Routes location={location}>
              {/* --- KEEP ALL YOUR ROUTES EXACTLY AS THEY ARE --- */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<LoginPage />} />

            <Route path="/kyc-sent/:id" element={<KycClientSideFormPage />} />
            <Route
              path="/kyc-sent-mobile/:id"
              element={<KycClientSideFormMobilePage />}
            />
            <Route path="/activate" element={<ActivateAccountPage />} />

            <Route
              path="/accept-disclaimer"
              element={
                <ProtectedRoute
                  loadingTitle="Loading…"
                  loadingSubtitle=""
                  loadingStyle="dots"
                  loadingBg="#EDF1F4"
                  allowDisclaimerPending
                >
                  <DisclaimerAcceptancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/c/documents-upload/:clientId"
              element={<ClientDocumentsUploadPage />}
            />
            <Route
              path="/c/documents-upload-mobile/:clientId"
              element={<ClientDocumentsUploadMobilePage />}
            />
            <Route
              path="/c/documents-upload/:clientId/thanks"
              element={<ClientUploadThanksPage />}
            />

            <Route
              path="/clients"
              element={
                <ProtectedRoute
                  loadingTitle="Signing you in..."
                  loadingSubtitle=""
                  loadingStyle="dots"
                  loadingBg="#EDF1F4"
                >
                  <ClientsPage />
                </ProtectedRoute>
              }
            />

            {["/fatca", "/fatca/w8bene", "/fatca/w8imy", "/fatca/classification", "/fatca/igas"].map((path) => <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute
                  loadingTitle="Loading…"
                  loadingSubtitle=""
                  loadingStyle="dots"
                  loadingBg="#EDF1F4"
                >
                  <Suspense fallback={<div role="status" className="text-center py-5">Loading form…</div>}>
                    <FatcaPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />)}

            <Route
              path="/regulatory"
              element={
                <ProtectedRoute
                  loadingTitle="Loading…"
                  loadingSubtitle=""
                  loadingStyle="dots"
                  loadingBg="#EDF1F4"
                >
                  <RegulatoryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/kyc/:id"
              element={
                <ProtectedRoute loadingTitle="Loading…" loadingSubtitle="" loadingStyle="dots" loadingBg="#EDF1F4">
                  <KycFormPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/kyc-bis-2/:id"
              element={
                <ProtectedRoute loadingTitle="Loading…" loadingSubtitle="" loadingStyle="dots" loadingBg="#EDF1F4">
                  <KycBis2Page />
                </ProtectedRoute>
              }
            />

            <Route
              path="/kyc-bis/:id"
              element={
                <ProtectedRoute loadingTitle="Loading…" loadingSubtitle="" loadingStyle="dots" loadingBg="#EDF1F4">
                  <KycBisPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/kyc-view/:id"
              element={
                <ProtectedRoute loadingTitle="Loading…" loadingSubtitle="" loadingStyle="dots" loadingBg="#EDF1F4">
                  <KycViewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/kyc-inbox"
              element={
                <ProtectedRoute loadingTitle="Loading…" loadingSubtitle="" loadingStyle="dots">
                  <KycInboxPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/kyc-review/:submissionId"
              element={
                <ProtectedRoute loadingTitle="Loading…" loadingSubtitle="" loadingStyle="dots" loadingBg="#EDF1F4">
                  <KycReviewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/docsReview/:docId"
              element={
                <ProtectedRoute loadingTitle="Loading…" loadingSubtitle="" loadingStyle="dots">
                  <StaffDocumentsReviewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <RequireAdmin>
                  <AdminUsersPage />
                </RequireAdmin>
              }
            />

            <Route
              path="/admin/risk-settings"
              element={
                <RequireManagerOrAdmin>
                  <AdminRiskSettingsPage />
                </RequireManagerOrAdmin>
              }
            />

            <Route
              path="/admin/sources"
              element={
                <RequireAdmin>
                  <AdminSourcesUploadPage />
                </RequireAdmin>
              }
            />

            <Route
              path="/admin/aml-policy"
              element={
                <RequireAdmin>
                  <AdminAmlPolicyPage />
                </RequireAdmin>
              }
            />

            <Route path="/admin/fatca" element={<RequireAdmin><AdminFatcaRulesPage /></RequireAdmin>} />

            <Route
              path="/ownershipTreeUae"
              element={
                <RequireAdmin>
                  <OwnershipTreeUae />
                </RequireAdmin>
              }
            />

            <Route
              path="*"
              element={<div className="p-4">404 - Not found</div>}
            />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </div>
      </StaffTopBandProvider>
    </AppLoadingProvider>
  );
}
